/* src/ui/windows/windowChrome.ts — draggable windows (TASKS.md 2.8, PLAN.md Part 4 §2).
 *
 * Every panel in the game — cart, inventory, market, leaderboards — is one of these. The
 * manager owns stacking and focus so windows cannot fight over z-index, and it reuses the
 * root dispatcher's `data-drag` contract rather than attaching per-window pointer listeners:
 * one set of window-level listeners serves N open windows, which is the whole reason
 * ui/dispatch.ts exists.
 *
 * Dragging deliberately does not use CSS transitions. A window must track the cursor exactly;
 * an eased position feels like input lag, not polish.
 */

import { on } from '../../core/events.ts';
import { el } from '../dom.ts';

export interface WindowOptions {
  readonly id: string;
  readonly title: string;
  /** Initial top-left, px. Defaults to a cascade so windows do not stack exactly. */
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly onClose?: () => void;
}

const MIN_MARGIN = 8;

export class WindowInstance {
  readonly id: string;
  readonly el: HTMLElement;
  readonly body: HTMLElement;
  /**
   * A slot for the panel's own controller, so a re-open can re-point the existing window instead
   * of rebuilding it. The window chrome does not care what this is — it is the panel's handle to
   * itself, kept here because the manager is what hands back an existing instance by id.
   */
  userData: unknown = undefined;
  readonly #onClose: (() => void) | undefined;

  constructor(opts: WindowOptions, private readonly manager: WindowManager) {
    this.id = opts.id;
    this.#onClose = opts.onClose;
    this.body = el('div', { class: 'gf-win-body' });

    this.el = el(
      'section',
      {
        class: 'gf-win',
        data: { windowId: opts.id },
        style: {
          left: `${String(opts.x ?? 0)}px`,
          top: `${String(opts.y ?? 0)}px`,
          width: `${String(opts.width ?? 340)}px`,
        },
      },
      el(
        'header',
        // data-drag is read by the root dispatcher, which emits ui:dragstart.
        { class: 'gf-win-title', data: { drag: 'window' } },
        el('span', { class: 'gf-win-name' }, opts.title),
        el(
          'button',
          {
            class: 'gf-win-close',
            attrs: { type: 'button', 'aria-label': `Close ${opts.title}` },
            data: { action: 'window:close', windowId: opts.id },
          },
          '×',
        ),
      ),
      this.body,
    );
  }

  focus(): void {
    this.manager.focus(this.id);
  }

  /** Re-title a reused window — a panel that re-points (fixture, building) renames itself. */
  setTitle(title: string): void {
    const name = this.el.querySelector('.gf-win-name');
    if (name) name.textContent = title;
  }

  close(): void {
    this.manager.close(this.id);
    this.#onClose?.();
  }

  setPosition(x: number, y: number): void {
    this.el.style.left = `${String(x)}px`;
    this.el.style.top = `${String(y)}px`;
  }
}

interface DragState {
  readonly instance: WindowInstance;
  readonly pointerId: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export class WindowManager {
  readonly #root: HTMLElement;
  readonly #windows = new Map<string, WindowInstance>();
  #drag: DragState | null = null;
  #z = 100;
  #cascade = 0;

  constructor(root: HTMLElement) {
    this.#root = root;

    on('ui:dragstart', ({ x, y, pointerId, target, kind }) => {
      if (kind !== 'window') return;
      const host = target.closest<HTMLElement>('[data-window-id]');
      const id = host?.dataset['windowId'];
      if (id === undefined) return;
      const instance = this.#windows.get(id);
      if (!instance) return;

      this.focus(id);
      const rect = instance.el.getBoundingClientRect();
      this.#drag = { instance, pointerId, offsetX: x - rect.left, offsetY: y - rect.top };
    });

    // Escape closes the topmost window, which is the one the player is looking at.
    on('ui:escape', () => {
      const top = [...this.#windows.values()].sort(
        (a, b) => Number(b.el.style.zIndex) - Number(a.el.style.zIndex),
      )[0];
      top?.close();
    });

    window.addEventListener('pointermove', this.#onMove);
    window.addEventListener('pointerup', this.#onUp);
    window.addEventListener('pointercancel', this.#onUp);
  }

  #onMove = (e: PointerEvent): void => {
    const drag = this.#drag;
    if (!drag || e.pointerId !== drag.pointerId) return;
    /*
     * Clamp so a window can never be dragged fully off-screen. Losing the titlebar past the
     * top edge is unrecoverable without a keyboard shortcut we do not have — so the titlebar
     * always stays reachable.
     */
    const maxX = window.innerWidth - MIN_MARGIN * 2;
    const maxY = window.innerHeight - MIN_MARGIN * 2;
    const x = Math.min(Math.max(e.clientX - drag.offsetX, -drag.instance.el.offsetWidth + 64), maxX);
    const y = Math.min(Math.max(e.clientY - drag.offsetY, 0), maxY);
    drag.instance.setPosition(x, y);
  };

  #onUp = (e: PointerEvent): void => {
    if (this.#drag && e.pointerId === this.#drag.pointerId) this.#drag = null;
  };

  has(id: string): boolean {
    return this.#windows.has(id);
  }

  get(id: string): WindowInstance | undefined {
    return this.#windows.get(id);
  }

  open(opts: WindowOptions): WindowInstance {
    const existing = this.#windows.get(opts.id);
    if (existing) {
      this.focus(opts.id);
      return existing;
    }

    // Cascade so a second window is visibly a second window rather than hiding the first.
    const step = (this.#cascade % 6) * 26;
    this.#cascade += 1;
    const instance = new WindowInstance(
      {
        ...opts,
        x: opts.x ?? 96 + step,
        y: opts.y ?? 96 + step,
      },
      this,
    );

    this.#windows.set(opts.id, instance);
    this.#root.appendChild(instance.el);
    this.focus(opts.id);
    return instance;
  }

  focus(id: string): void {
    const instance = this.#windows.get(id);
    if (!instance) return;
    this.#z += 1;
    instance.el.style.zIndex = String(this.#z);
    for (const [otherId, other] of this.#windows) {
      other.el.classList.toggle('is-active', otherId === id);
    }
  }

  close(id: string): void {
    const instance = this.#windows.get(id);
    if (!instance) return;
    this.#windows.delete(id);
    instance.el.remove();
  }

  closeAll(): void {
    for (const id of [...this.#windows.keys()]) this.close(id);
  }
}
